import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, provideAppInitializer, inject } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling } from '@angular/router';
import { providePrimeNG } from 'primeng/config';
import { appRoutes } from './app.routes';
import { definePreset } from '@primeng/themes';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { AuthInterceptor } from './auth-interceptor';
import Aura from '@primeng/themes/aura';
import { MessageService } from 'primeng/api';
import { AuthSyncService } from './app/core/auth/auth-sync.service';
import { TranslationService } from './app/core/i18n/translation.service';

const MyPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{indigo.50}',
            100: '{indigo.100}',
            200: '{indigo.200}',
            300: '{indigo.300}',
            400: '{indigo.400}',
            500: '{indigo.500}',
            600: '{indigo.600}',
            700: '{indigo.700}',
            800: '{indigo.800}',
            900: '{indigo.900}',
            950: '{indigo.950}'
        },
        formField: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            sm: {
                fontSize: '0.875rem',
                paddingX: '0.375rem',
                paddingY: '0.25rem'
            },
            lg: {
                fontSize: '1.125rem',
                paddingX: '0.625rem',
                paddingY: '0.5rem'
            }
        }
    },
    components: {
        // Text-based inputs
        inputtext: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },
        textarea: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },
        inputnumber: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },
        password: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },
        inputmask: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },
        inputotp: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },

        // Dropdown/Select inputs
        select: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },
        dropdown: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },
        multiselect: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },
        autocomplete: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },
        cascadeselect: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },
        treeselect: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },
        listbox: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },

        // Date/Time inputs
        calendar: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem',
            zIndex: 10000,
        },
        datepicker: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },

        // Boolean/Toggle inputs
        checkbox: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            width: '1.5rem',
            height: '1.5rem',
            borderRadius: '0.25rem'
        },
        radiobutton: {
            width: '1.5rem',
            height: '1.5rem'
        },
        togglebutton: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },
        toggleswitch: {
            width: '2rem',
            height: '1rem',
            borderRadius: '1rem'
        },
        inputswitch: {
            width: '2rem',
            height: '1rem',
            borderRadius: '1rem'
        },
        tristatecheckbox: {
            width: '1rem',
            height: '1rem',
            borderRadius: '0.25rem'
        },

        // Selection inputs
        selectbutton: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },
        inputchips: {
            root: {
                paddingX: '0.5rem',
                paddingY: '0.40625rem',
                borderRadius: '0.375rem'
            },
            chip: {
                borderRadius: '0.375rem',
                color: '{surface.800}'
            }
        },
        chip: {
            paddingX: '0.5rem',
            paddingY: '0.25rem',
            borderRadius: '0.375rem',
            color: '{surface.800}'
        },

        // Range/Slider inputs
        slider: {
            trackHeight: '0.375rem',
            handleWidth: '1rem',
            handleHeight: '1rem',
            borderRadius: '0.375rem'
        },
        knob: {
            size: '6rem',
            fontSize: '0.875rem'
        },

        // Rating input
        rating: {
            iconSize: '1rem',
            gap: '0.25rem'
        },

        // File input
        fileupload: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },

        // Color input
        colorpicker: {
            paddingX: '0.5rem',
            paddingY: '0.375rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        },

        // Editor
        editor: {
            toolbarPadding: '0.5rem',
            fontSize: '0.875rem',
            borderRadius: '0.375rem'
        }
    }
});

export const appConfig: ApplicationConfig = {
    providers: [
        MessageService,
        provideAppInitializer(() => {
            inject(AuthSyncService).init();
        }),
        provideAppInitializer(() => inject(TranslationService).initializeLanguage()),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({
                anchorScrolling: 'enabled',
                scrollPositionRestoration: 'enabled'
            }),
            withEnabledBlockingInitialNavigation()
        ),
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        provideAnimationsAsync(),
        providePrimeNG({
            ripple: true,
            inputStyle: 'outlined',
            theme: { preset: MyPreset, options: { darkModeSelector: '.app-dark' } },
            zIndex: {
                modal: 1100,
                overlay: 1000,
                menu: 1000,
                tooltip: 1100
            }
        })
    ]
};
